import "./AccountDropdown.scss";
import * as React from "react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { CircularProgress, Menu, OutlinedInput, Typography } from "@mui/material";
import {
  APP_HOTELS_API_ENDPOINT,
} from "../../../constants/api_urls";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  updateAccount,
  updateAccountList,
  updateCanUseGroups,
  updateGroupId,
  updateGroupList,
  updateHotelId,
  updateHotelList,
  updateLevel,
} from "store/slices/account/accountSlice";
import { useNavigate, useLocation, createSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { setActiveSection } from "../../../store/slices/sidebar/sidebar";
import { getUserToken } from "../../../utils/functions/userToken";
import { useGetAccountList } from "hooks";
import { CancelOutlined, Search } from "@mui/icons-material";
import { useVirtual } from "react-virtual";
import queryString from "query-string";

export default function AccountDropdown() {
  const { hotelsList, accountList } = useGetAccountList();
  const selectedAccount = useSelector((state) => state?.account?.account || {});
  const {
    hotel_id = [],
    accounts = [],
    level,
    accountsStatus,
  } = useSelector((state) => state?.account || {});
  const [selectedValues, setSelectedValues] = useState(null);
  const [hasUserSelected, setHasUserSelected] = useState(false);
  const [searchedAccounts, setSearchedAccounts] = React.useState([]);
  const [searchedHotels, setSearchedHotels] = React.useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const token = getUserToken() || "";
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { idaccount } = useParams();
  const searchParams = queryString.parse(window.location.search)
  const dispatch = useDispatch();

  // "idle" covers the first render, before the request has been fired.
  const isLoadingAccounts =
    accountsStatus === "idle" || accountsStatus === "loading";

  const parentRef = React.useRef();
  const rowVirtualizer = useVirtual({
    size: searchedAccounts.length,
    parentRef,
    estimateSize: React.useCallback(() => 30, []),
    overscan: 20
  });

  const hotelParentRef = React.useRef();
  const rowVirtualizerHotels = useVirtual({
    size: searchedHotels.length,
    parentRef: hotelParentRef,
    estimateSize: React.useCallback(() => 30, []),
    overscan: 20
  });

  React.useEffect(() => {
    if (!accounts?.length && accountList.length) {
      dispatch(updateAccountList(accountList));
      setSearchedAccounts(accountList);
    }
  }, [accounts, dispatch, accountList]);

  // Keeps the rendered list in sync with the loaded accounts. Without this, a
  // menu that is already open when the request resolves keeps showing an empty
  // list, because the effect above only fires while `accounts` is still empty.
  React.useEffect(() => {
    if (!searchTerm.length) {
      setSearchedAccounts(accounts);
    }
  }, [accounts, searchTerm]);

  React.useEffect(() => {
    if (hotelsList.length) {
      setSearchedHotels(hotelsList);
    }
  }, [hotelsList]);

  React.useEffect(() => {
    const searchedAccountId = idaccount;
    if (searchedAccountId && searchedAccountId !== "undefined") {
      const searchedAccount = accounts?.find(
        (ac) => ac.account_id.toString() === searchedAccountId.toString()
      );

      if (searchedAccount) {
        setSelectedValues(searchedAccount);
        dispatch(updateAccount(searchedAccount));
        if (searchedAccount?.account_id) {
          axios
            .get(
              `${APP_HOTELS_API_ENDPOINT}/api/hotels/list?id_account=${searchedAccount?.account_id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            )
            .then((res) => {
              const page = window.location.pathname;
              dispatch(updateHotelList(res?.data?.data || []));
              const isHotelIdCorrect = Array.from(hotel_id || []).every((id) =>
                res?.data?.data.map((h) => h?.["hotel_id"])?.includes(id)
              );
              if (
                res?.data?.data.length > 0 &&
                (!isHotelIdCorrect || !hotel_id.length) &&
                (level !== "GROUP" ||
                  page.includes("searchads") ||
                  page.includes("pMax"))
              ) {
                dispatch(
                  updateHotelId(res?.data?.data.map((h) => h?.["hotel_id"]))
                );
                dispatch(updateGroupId([]));
              }
            });

          axios
            .get(
              `${APP_HOTELS_API_ENDPOINT}/api/group/list?id_account=${searchedAccount?.account_id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            )
            .then((res) => {
              dispatch(updateGroupList(res?.data?.["group_list"] || []));
              dispatch(updateCanUseGroups(res?.data?.["can_use_groups"]));
            });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idaccount, JSON.stringify(accounts), dispatch, token]);

  React.useEffect(() => {
    if (Object.keys(selectedAccount || {}).length) {
      setSelectedValues(selectedAccount);
    }
  }, [selectedAccount]);

  const handleSearch = (e) => {
    const search = e.target.value.toLowerCase();
    setSearchTerm(search);
    if (search === "a" || search === "A") {
      e?.preventDefault();
    }
    if (search.length === 0) {
      setSearchedAccounts(accounts);
      setSearchedHotels(hotelsList);
      return;
    }

    const tempData = [...accounts];
    const hotelsArray = [...hotelsList];
    setSearchedAccounts(
      tempData?.filter((item) => {
        const accountId = item?.account_id?.toString().toLowerCase();
        const accountName = item?.account_name?.toString().toLowerCase();
        if (item?.search_by_hotel === true) {
          const hotelIds = item?.hotel_ids?.toString().toLowerCase();
          return !!(
            accountId?.includes(search) ||
            accountName?.includes(search) ||
            hotelIds?.includes(search)
          );
        } else {
          return !!(
            accountId?.includes(search) || accountName?.includes(search)
          );
        }
      })
    );

    setSearchedHotels(
      hotelsArray.filter((hotel) => {
        const hotelid = hotel.hotel_id.toString().toLowerCase();
        const hotelname = hotel.hotel_name.toString().toLowerCase();

        return !!(hotelid.includes(search) || hotelname.includes(search));
      })
    );
  };
  const [isOpen, setIsOpen] = React.useState(false);
  const [expand, setExpand] = useState(false);
  const accountRef = React.useRef(null);
  const [hoveredItem, setHoveredItem] = useState("");

  useEffect(() => {
    if (searchedAccounts.length > 0 && !hasUserSelected && !selectedAccount) {
      const lastAccount = searchedAccounts[searchedAccounts.length - 1];
      setSelectedValues(lastAccount);
      setHasUserSelected(true);
      dispatch(updateAccount(lastAccount));
    }
  }, [searchedAccounts, hasUserSelected, selectedAccount, dispatch]);

  const handleAccountClick = (account, ishotelClick = false) => {
    dispatch(updateAccount(account));
    dispatch(updateLevel("HOTEL"));
    dispatch(updateGroupId([]));
    if(!ishotelClick){
      dispatch(updateHotelId([]));
    }
    setSearchTerm("")

    const redirectToHomeRoutes = [
      "bidding-offline",
      "groups",
      "searchads",
      "sign-up-sem",
      "sign-up-pmax",
      "sign-up-display-ads",
      "sign-up-meta-travel-ads",
      "bidding-center",
      "search-ads-campaign",
      "search-ads-campaign/create",
      "pmax-campaign-manager",
      "pmax-campaign-manager/create",
      "keywords-reporting",
      "search-terms-reporting",
      "display-ads-campaign-manager",
      "display-ads-campaign-manager/create",
      "meta-travel-ads-campaign-manager",
      "meta-travel-ads-campaign-manager/create",
      "transition-admin"
    ];

    const basePath = pathname.split("/")[1];

    // const query = queryString.stringify(queryString.parse(window.location.search));
    const accountId = account?.account_id;

    if (!accountId) return;
    const targetPath = redirectToHomeRoutes.includes(basePath)
      ? `/home/${accountId}`
      : pathname.replace(/\/\d+(?=\/|$)/, `/${accountId}`);

    dispatch(setActiveSection(null));
    navigate(`${targetPath}`);
    setIsOpen(false);
  }

  return (
    <Box sx={{ minWidth: 120 }} className={"account"}>
      <FormControl sx={{ wdith: 240 }} className="accountDropdown">
        <div
          className="selected"
          ref={accountRef}
          onClick={() => {
            setSearchedAccounts(accounts);
            setSearchedHotels(hotelsList);
            setExpand(true);
            setTimeout(() => {
              setIsOpen(!isOpen);
            }, 300);
          }}
        >
          <div
            className="selected-label"
            onMouseEnter={() => setHoveredItem("accountDropDown")}
            onMouseLeave={() => setHoveredItem("")}
          >
            <div
              className="accountHoverState"
              style={{
                backgroundColor:
                  hoveredItem === "accountDropDown" ? "#61DDAE" : "#61D5A9",
                border:
                  hoveredItem === "accountDropDown"
                    ? "1px solid #000"
                    : "1px solid #000",
                width: expand ? 400 : "fit-content",
                minWidth: 240,
                transition: "width 0.3s",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              <div style={{ paddingLeft: "10px", width: "fit-content" }}>
                {selectedValues?.["account_name"] ? (
                  <>
                    <span style={{ marginRight: "12px" }}>
                      {selectedValues?.["account_name"]?.toUpperCase()}
                    </span>
                    {!!Number(selectedValues?.["hotel_count"]) && <span>({selectedValues?.["hotel_count"]})</span>}
                  </>
                ) : (
                  "Account"
                )}
              </div>
              <Search sx={{ fontSize: "18px", marginRight: "8px" }} />
            </div>
          </div>{" "}
        </div>
        <Menu
          open={isOpen}
          anchorEl={accountRef.current}
          onClose={() => {
            setIsOpen(false);
            setSearchTerm("");
            setExpand(false);
          }}
          slotProps={{
            paper: {
              sx: {
                marginTop: "-25px",
                minWidth: 190,
                width: 400,
                borderRadius: "12px",
                overflowX: "hidden",
                whiteSpace: "nowrap",
                color: "#000000",
                fontFamily: "Inter",
              },
            },
          }}
        >
          <div>
            <OutlinedInput
              sx={{
                borderRadius: "10px",
                borderColor: "#CADEDD",
                width: "380px",
                margin: "0 10px",
                height: "28px",
                fontSize: "12px",
                fontWeight: "400",
                fontFamily: "inter",
              }}
              value={searchTerm}
              size="small"
              placeholder="Search by account or hotel"
              endAdornment={
                searchTerm ? (
                  <span
                    style={{ display: "flex" }}
                    onClick={() => {
                      handleSearch({ target: { value: "" } });
                    }}
                  >
                    <CancelOutlined
                      className="clear-icon"
                      sx={{
                        color: "#CADEDD",
                        fontSize: "18px",
                        cursor: "pointer",
                      }}
                    />
                  </span>
                ) : (
                  <Search sx={{ color: "#CADEDD", fontSize: "18px" }} />
                )
              }
              onChange={(e) => {
                handleSearch(e);
              }}
              onKeyDown={e => {e.stopPropagation()}}
            />
          </div>
          <Box
            ref={parentRef}
            sx={{
              maxHeight: "300px",
              overflowY: "auto",
              marginRight: "5px",
              marginTop: "8px",
              overflowX: "hidden",
              transition: "max-height 0.3s ease",
            }}
          >
            <p className="title-list">Accounts</p>
            {searchedAccounts.length === 0 &&
              (isLoadingAccounts ? (
                <Box sx={{ display: "flex", justifyContent: "center", padding: "6px 0" }}>
                  <CircularProgress size={16} />
                </Box>
              ) : (
                <Typography
                  sx={{
                    padding: "6px 16px",
                    fontSize: "12px",
                    fontWeight: "400",
                    fontFamily: "inter",
                    color: "#373F41",
                    whiteSpace: "normal",
                  }}
                >
                  {accounts.length === 0
                    ? "No account available."
                    : "No matching account."}
                </Typography>
              ))}
            <div
              style={{
                height: `${rowVirtualizer.totalSize}px`,
                width: "100%",
                position: "relative",
                overflow: "auto",
              }}
            >
              {rowVirtualizer.virtualItems?.map((item) => {
                const account = searchedAccounts[item.index];
                return (
                  <div
                    key={item.index}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      height: `${item.size}px`,
                      width: "100%",
                      transform: `translateY(${item.start}px)`,
                    }}
                  >
                    <MenuItem
                      sx={{
                        fontSize: "12px",
                        fontWeight: "400",
                        fontFamily: "inter",
                        color: "#000000",
                      }}
                      value={account.account_id}
                      onClick={() => {
                        handleAccountClick(account);
                      }}
                    >
                      <div className={`menu-item-account`}>
                        <span
                          className={`${
                            account.account_status === "ACTIVE"
                              ? "active"
                              : "account-status"
                          }`}
                        />
                        {account.account_name}
                        {!!account.hotel_count && (
                          <span className="hotel-count">
                            ({account.hotel_count})
                          </span>
                        )}
                      </div>
                    </MenuItem>
                  </div>
                );
              })}
            </div>
          </Box>

          {!!searchTerm.length && (
            <Box
              ref={hotelParentRef}
              sx={{
                maxHeight: "300px",
                overflowY: "auto",
                marginRight: "5px",
                marginTop: "8px",
                overflowX: "hidden",
                transition: "max-height 0.3s ease",
              }}
            >
              <p className="title-list">Hotels</p>
              <div
                style={{
                  height: `${rowVirtualizerHotels.totalSize}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {rowVirtualizerHotels.virtualItems?.map((item) => {
                  const hotel = searchedHotels[item.index];
                  return (
                    <div
                      key={item.index}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: `${item.size}px`,
                        width: "100%",
                        transform: `translateY(${item.start}px)`,
                      }}
                    >
                      <MenuItem
                        sx={{
                          fontSize: "12px",
                          fontWeight: "400",
                          fontFamily: "inter",
                          color: "#000000",
                        }}
                        value={hotel.hotel_id}
                        onClick={() => {
                          dispatch(updateHotelId([hotel.hotel_id]));
                          const selectedAcc = accountList.find(
                            (acc) => acc.account_id === hotel.account_id
                          );
                          handleAccountClick(selectedAcc, true);
                          setSearchTerm("");
                          setIsOpen(false);
                          const newPath =
                            pathname === "/transition-admin"
                              ? `/home/${selectedAcc.account_id}`
                              : pathname.replace(
                                  /\/\d+(?=\/|$)/,
                                  `/${selectedAcc.account_id}`
                                );

                          const newSearch = createSearchParams({
                            ...searchParams,
                            hotelid: [hotel.hotel_id],
                          });
                          navigate(`${newPath}?${newSearch}`);

                        }}
                      >
                        <div className={`menu-item-account`}>
                          <span
                            className={`${
                              hotel.hotel_status === "ACTIVE"
                                ? "active"
                                : "account-status"
                            }`}
                          />
                          {hotel.hotel_name}
                        </div>
                      </MenuItem>
                    </div>
                  );
                })}
              </div>
            </Box>
          )}
        </Menu>
      </FormControl>
    </Box>
  );
}
